import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { catchError, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../base-component/base.component';
import { AssignShipmentAction, RunningShipmentCheckType } from '@shared-features/enums';
import { SharedAPI } from '@shared-features/services/shared-api.service';
import { Shipment } from '@features/shared-features/models/shipment.model';
import { forkJoin, Observable, of } from 'rxjs';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'app-shipment-assignment-action-wrapper',
  templateUrl: './shipment-assignment-action-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShipmentAssignmentActionWrapperComponent extends BaseComponent implements OnChanges {
  @Input() shipmentId: number;
  @Input() isViewActions: boolean = true;
  @Input() driverId: number;
  @Input() truckId: number;
  @Input() form: FormGroup;

  @Output() conflictDetected = new EventEmitter<boolean>();

  @Output() assignShipmentActionChange = new EventEmitter<{
    assignShipmentAction: AssignShipmentAction;
    justification: string;
  }>();

  driverRunningShipment: Shipment;
  truckRunningShipment: Shipment;

  constructor(private changeDetectorRef: ChangeDetectorRef, private sharedApiService: SharedAPI) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.driverId || changes.truckId) {
      this.checkRunningShipments();
    }
  }

  /**
   * Both checks go out together on one `load()`, so `sectionState` covers the whole wait rather
   * than clearing the moment the faster of the two answers.
   */
  private checkRunningShipments(): void {
    this.driverRunningShipment = null;
    this.truckRunningShipment = null;

    if (!this.driverId && !this.truckId) {
      this.emitConflict();
      return;
    }

    this.load(
      forkJoin({
        driver: this.runningShipmentFor(this.driverId, RunningShipmentCheckType.Driver),
        truck: this.runningShipmentFor(this.truckId, RunningShipmentCheckType.Truck),
      }),
      { isLoadingTransparent: true }
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ driver, truck }) => {
        this.driverRunningShipment = driver;
        this.truckRunningShipment = truck;
        this.emitConflict();
        this.changeDetectorRef.markForCheck();
      });
  }

  /** A missing id is not an error, it just has no shipment to clash with. */
  private runningShipmentFor(id: number, type: RunningShipmentCheckType): Observable<Shipment> {
    if (!id) {
      return of(null);
    }
    const params = type === RunningShipmentCheckType.Driver ? { driverId: id, type } : { truckId: id, type };
    return this.sharedApiService.getRunningShipments(params).pipe(catchError(() => of(null)));
  }

  private emitConflict(): void {
    this.conflictDetected.emit(!!(this.driverRunningShipment || this.truckRunningShipment));
  }
}
